import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import classNames from 'classnames';
import OpportunityQuickFilter from '@/constants/opportunities/OpportunityQuickFilter';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useOpportunities from '@/hooks/swr/useOpportunities';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import type OpportunityConfidenceLevel from '@/types/api/OpportunityConfidenceLevel';
import type PutOpportunityConfidenceLevel from '@/types/api/PutOpportunityConfidenceLevel';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';
import useConfidenceLevelForm from '../hooks/useConfidenceLevelForm';
import useConfidenceLevelServices from '../hooks/useConfidenceLevelServices';
import ConfidenceLevelForm from './ConfidenceLevelForm';

type ConfidenceLevelProps = {
  opportunityConfidenceLevel: OpportunityConfidenceLevel;
};

function ConfidenceLevel({ opportunityConfidenceLevel }: ConfidenceLevelProps) {
  const history = useHistory();
  const { amount, name, confidenceLevelId } = opportunityConfidenceLevel;
  const { openDialog, closeDialog } = useDialogManager();
  const { data, isLoading } = useOpportunities({
    filter: { confidenceLevelId },
  });
  const { deleteConfidenceLevel, updateConfidenceLevel } =
    useConfidenceLevelServices();
  const [isEditing, setIsEditing] = React.useState(false);

  const handleEditButtonClick = () => {
    setIsEditing(!isEditing);
  };

  const handleDeleteButtonClick = React.useCallback(() => {
    if (data?.totalHits) {
      openDialog(Dialogs.CONFIRMATION, {
        children: translate('CONFIDENCE_LEVEL_CAN_NOT_DELETE', {
          count: data.totalHits,
        }),
        onClose: closeDialog,
        cancelLabel: translate('CANCEL'),
        onCancel: closeDialog,
        confirmLabel: translate('OPPORTUNITIES_VIEW'),
        confirmButtonColor: 'primary',
        onConfirm: () => {
          closeDialog();
          history.push({
            pathname: AuthRoutes.OPPORTUNITIES,
            search: new URLSearchParams({
              [OpportunityQuickFilter.CONFIDENCE_LEVEL_IDS]:
                opportunityConfidenceLevel.confidenceLevelId,
            }).toString(),
          });
        },
      });
    } else {
      openDialog(Dialogs.CONFIRMATION, {
        children: translate('CONFIDENCE_LEVEL_DELETE_CONFIRM_MSG'),
        confirmLabel: translate('DELETE'),
        onCancel: closeDialog,
        onClose: closeDialog,
        onConfirm: async () => {
          try {
            await deleteConfidenceLevel(
              opportunityConfidenceLevel.confidenceLevelId,
            );
            showSuccessMessage(translate('CONFIDENCE_LEVEL_DELETE_SUCCESS'));
            closeDialog();
          } catch (e) {
            showErrorMessage(translate('CONFIDENCE_LEVEL_DELETE_FAILURE'));
          }
        },
      });
    }
  }, [data]);

  const handleFormSubmit = async (values: PutOpportunityConfidenceLevel) => {
    try {
      await updateConfidenceLevel(confidenceLevelId, values);
      setIsEditing(false);
      showSuccessMessage(translate('CONFIDENCE_LEVEL_UPDATE_SUCCESS'));
    } catch {
      showErrorMessage(translate('CONFIDENCE_LEVEL_UPDATE_FAILURE'));
    }
  };

  const { formMethods, defaultValues } = useConfidenceLevelForm({
    amount,
    name,
  });
  const { reset, handleSubmit } = formMethods;

  const handleCancel = React.useCallback(() => {
    setIsEditing(false);
    reset(defaultValues);
  }, [reset, defaultValues]);

  return (
    <div
      className="grid max-w-5xl grid-cols-[1fr_96px] gap-4"
      data-testid="confidence-level-row"
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <ConfidenceLevelForm onCancel={handleCancel} isEditing={isEditing} />
        </form>
      </FormProvider>
      <div>
        <IconButton
          aria-label={translate('CONFIDENCE_LEVEL_EDIT_LEVEL')}
          type="button"
          onClick={handleEditButtonClick}
          size="large"
          className="text-text-primary"
        >
          <EditIcon />
        </IconButton>
        <IconButton
          aria-label={translate('CONFIDENCE_LEVEL_REMOVE_LEVEL')}
          type="button"
          onClick={handleDeleteButtonClick}
          size="large"
          className={classNames('pointer-events-auto text-text-primary', {
            'cursor-wait': isLoading,
          })}
          disabled={isLoading}
          data-testid="confidence-level-remove"
        >
          <DeleteIcon />
        </IconButton>
      </div>
    </div>
  );
}

export default ConfidenceLevel;
